import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';
import { Component } from '@theme/component';

/**
 * <stmz-availability> — per-variant fulfillment status (in-stock / backorder / sold-out).
 *
 * On variant change the theme dispatches `variant:update` on the section with the
 * freshly server-rendered section HTML in `event.detail.data.html`. We pull the
 * matching <stmz-availability> from that HTML and morph our children in — so the
 * Liquid in `snippets/stmz-availability.liquid` stays the single source of truth
 * for the state logic (no duplicated rules in JS). Mirrors assets/product-inventory.js.
 *
 * This component is display-only: it swaps the status line on variant change and
 * never touches the Add-to-Cart button, whose state is left entirely to the theme.
 * (The old pre-order acknowledgment checkbox and its Add-to-Cart gate were removed
 * 2026-09-07.) The backorder state is driven purely by the variant's inventory
 * policy — see snippets/stmz-availability.liquid.
 */
class StmzAvailability extends Component {
  connectedCallback() {
    super.connectedCallback();
    const section = this.closest('.shopify-section, dialog');
    section?.addEventListener(ThemeEvents.variantUpdate, this.update);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const section = this.closest('.shopify-section, dialog');
    section?.removeEventListener(ThemeEvents.variantUpdate, this.update);
  }

  /**
   * @param {CustomEvent} event - The variant:update event.
   */
  update = (event) => {
    // Combined-listing safety: track product id, ignore events for other products.
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (
      event.target instanceof HTMLElement &&
      event.target.dataset.productId &&
      this.dataset.productId &&
      event.target.dataset.productId !== this.dataset.productId
    ) {
      return;
    }

    const next = event.detail.data.html?.querySelector('stmz-availability');
    if (!next) return;

    morph(this, next, { childrenOnly: true });
  };
}

if (!customElements.get('stmz-availability')) {
  customElements.define('stmz-availability', StmzAvailability);
}
